import React, { FC, useMemo } from 'react';
import cx from 'classnames';
import { Navigation as NavigationType } from '../../types';
import { useCurrentUser } from '../../utils/currentUser';
import matchesCondition from '../../utils/conditions';
import styles from './Navigation.css';
import { useFeatures } from '../../utils/features';
import NavigationItem from './NavigationItem';

type Props = {
  classNames?: {
    root?: string;
    action?: string;
    link?: string;
  };
  navigation?: NavigationType;
  onClick?: (
    name: string,
    details?: { [key: string]: string | undefined },
    event?: Event
  ) => void;
};

const Navigation: FC<Props> = ({
  classNames,
  children,
  navigation = [],
  onClick
}): JSX.Element => {
  const { isLoggedIn } = useCurrentUser();
  const featureFlags = useFeatures();

  const enabledNavigation = useMemo(
    () =>
      navigation?.filter(
        (navigationItem) =>
          !navigationItem?.condition ||
          matchesCondition(navigationItem?.condition, {
            featureFlags,
            isLoggedIn
          })
      ),
    [navigation, isLoggedIn, featureFlags]
  );

  return (
    <div
      className={cx(styles.component, classNames?.root)}
      id="headerMenu"
      data-testid="HeaderMenu"
      role={
        enabledNavigation?.length || Boolean(children) ? 'menubar' : undefined
      }
    >
      {enabledNavigation?.map((navigationItem, navigationIndex) => (
        <NavigationItem
          key={navigationItem.name}
          classNames={{
            root: cx({
              [styles.minWidthSmall]:
                navigationItem.condition?.mediaQuery === 'small',
              [styles.minWidthMedium]:
                navigationItem.condition?.mediaQuery === 'medium',
              [styles.minWidthLarge]:
                navigationItem.condition?.mediaQuery === 'large',
              [styles.maxWidthSmall]:
                navigationItem.condition?.mediaQuery === '!small',
              [styles.maxWidthMedium]:
                navigationItem.condition?.mediaQuery === '!medium',
              [styles.maxWidthLarge]:
                navigationItem.condition?.mediaQuery === '!large'
            }),
            action: classNames?.action,
            link: classNames?.link
          }}
          index={navigationIndex}
          length={enabledNavigation.length}
          navigationItem={navigationItem}
          onClick={onClick}
        />
      ))}
      {children && (
        <div className={styles.item} role="menuitem">
          {children}
        </div>
      )}
    </div>
  );
};

export default Navigation;
